import { IS_NOT_ANONYMOUS_REQUIREMENTS } from '@constants';
import { CustomerInput } from '@hello-pimster/pimster-crm-sdk';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useApiCrm } from '@lib/pimster-crm/provider/ApiCrmProvider';
import { isCustomerInput } from '@lib/pimster-crm/types/guards';
import { useQuery } from '@tanstack/react-query';
import { objectHasOneOfKeys } from '@utils/objectHasKey';
import * as Axios from 'axios';
import { isBoolean } from 'lodash';
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from 'react';

interface UserContextProps {
    isAuthenticated: boolean;
}

const UserContext = createContext<UserContextProps>({} as UserContextProps);

export const UserProvider = ({ children }: PropsWithChildren) => {
    const { userAnonymousId } = useAnalytics();
    const { client, addInterceptor } = useApiCrm();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    const {
        data: me,
        isLoading,
        isError,
    } = useQuery({
        enabled: !!userAnonymousId,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        queryKey: ['myCustomerProfile'],
        queryFn: () =>
            client.customerProfileControllerFindMe({
                cookieId: userAnonymousId!,
            }),
        staleTime: Infinity,
    });

    useEffect(() => {
        if (!isLoading && !isError && me && isBoolean(me.data.isAnonymous)) {
            setIsAuthenticated(!me.data.isAnonymous);
        }
    }, [me, isLoading, isError]);

    useEffect(() => {
        addInterceptor({
            fulfilled: (res: Axios.AxiosResponse<CustomerInput>) => {
                const hasJustAuthenticated = objectHasOneOfKeys(
                    res.data.data,
                    IS_NOT_ANONYMOUS_REQUIREMENTS
                );

                if (hasJustAuthenticated) {
                    setIsAuthenticated(true);
                }

                return res;
            },
            condition: (res) => isCustomerInput(res.data),
        });
    }, [addInterceptor]);

    return (
        <UserContext.Provider value={{ isAuthenticated }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
